import { useState, useEffect } from "react";
import { useSearchParams, useNavigate, createSearchParams } from "react-router-dom";
import { useRecoilState } from "recoil";
import { currentPostAtom } from "../atom";
import { getPostData, searchPost, PostSearchParam } from "../api/postAPI";

import styled from "styled-components";
import { Spin, Empty } from "antd";
import { LockOutlined } from "@ant-design/icons";

type GroupedPosts = {
  [boardName: string]: {
    [mainCategory: string]: {
      [subCategory: string]: SearchedPost[];
    };
  };
};

export interface SearchedPost {
  id: number;
  title: string;
  boardId: number;
  categoryType: string;
  parentName: string | null;
  grandParentName: string | null;
  grandGrandParentName: string | null;
  // B안: 각 이름과 짝이 되는 카테고리 id (백엔드에서 함께 내려줌)
  parentId: number | null;
  grandParentId: number | null;
  grandGrandParentId: number | null;
  createdAt: string;
  updatedAt: string;
  isSecret: boolean;
  // 본문에서 검색어가 매칭된 경우의 발췌. 제목에서만 매칭됐으면 null
  snippet?: string | null;
  // 검색어(숫자)와 post id 가 일치해 id 로 검색된 결과임을 표시 (프론트에서 부여)
  isIdMatch?: boolean;
}

// 질환 카테고리(/portal)에 해당하는 최상위 게시판(board)명. 이 board 는 portal 트리로,
// 그 외 board 는 dedicated 페이지 트리로 breadcrumb 를 구성한다.
const PORTAL_BOARD_NAME = "Organ";

const BOARD_LABEL_MAP: Record<string, string> = {
  Consultation: "Consultation/Discussion",
};

const getBoardLabel = (name: string | null | undefined): string => {
  if (!name) return "";
  return BOARD_LABEL_MAP[name] ?? name;
};

/**
 * 검색어 토큰을 <mark> 로 감싼다.
 * 검색어가 그대로 HTML 이 되지 않도록 dangerouslySetInnerHTML 대신 엘리먼트 배열로 렌더한다.
 */
const highlight = (text: string, keyword: string): React.ReactNode => {
  const tokens = keyword.trim().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return text;

  // 검색어에 괄호나 + 가 들어가면 (예: "(CML)", "C++") 패턴이 깨지므로 이스케이프한다
  const escaped = tokens.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  const pattern = new RegExp(`(${escaped.join("|")})`, "gi");
  const lowered = tokens.map((t) => t.toLowerCase());

  // split 에 캡처 그룹을 쓰면 매칭된 조각도 배열에 남는다
  return text
    .split(pattern)
    .map((part, i) =>
      lowered.includes(part.toLowerCase()) ? <mark key={i}>{part}</mark> : part
    );
};

const SearchPage = () => {
  const [searchParams] = useSearchParams();
  const [groupedData, setGroupedData] = useState<GroupedPosts>({});
  const [loading, setLoading] = useState(false);
  const [keyword, setKeyword] = useState("");
  // 오타 교정 시 실제 검색에 쓰인 검색어 (하이라이트 대상)
  const [searchedKeyword, setSearchedKeyword] = useState("");
  // 교정이 일어난 경우 사용자가 원래 입력했던 검색어
  const [originalKeyword, setOriginalKeyword] = useState<string | null>(null);
  const [currentPost, setCurrentPost] = useRecoilState(currentPostAtom);

  const navigate = useNavigate();

  useEffect(() => {
    const title = searchParams.get("title");
    if (title) {
      setKeyword(title);
      // autoCorrect=false 로 들어오면 오타 교정 없이 입력한 검색어 그대로 조회한다
      getSearchHandler({ title }, searchParams.get("autoCorrect") !== "false");
    }
  }, [searchParams]);

  const getSearchHandler = async (params: PostSearchParam, autoCorrect: boolean = true) => {
    setLoading(true);
    try {
      const result = await searchPost(params.title || "", autoCorrect);
      const searchResult = (result?.posts ?? []) as SearchedPost[];

      // 오타 교정이 일어났으면 교정된 검색어로 하이라이트하고 안내를 띄운다
      setSearchedKeyword(result?.searchedKeyword || params.title || "");
      setOriginalKeyword(result?.originalKeyword ?? null);

      // ================================================================
      // [임시 조치] 특정 게시판 제외 필터링
      // - "학생 병원병리학 실습" 게시판은 검색 결과에서 제외
      // - 추후 삭제 예정
      const filteredResult = searchResult.filter(
        (post) =>
          post.grandParentName !== "학생 병원병리학 실습"
      );
      // ================================================================

      // 백엔드는 검색어와 일치하는 post id 를 결과의 맨 앞에 배치한다.
      // 검색어가 숫자이고 첫 결과의 id 와 일치하면 "id 로 검색된 결과"로 표시한다.
      const trimmedKeyword = (params.title || "").trim();
      if (
        /^\d+$/.test(trimmedKeyword) &&
        filteredResult.length > 0 &&
        filteredResult[0].id === Number(trimmedKeyword)
      ) {
        filteredResult[0] = { ...filteredResult[0], isIdMatch: true };
      }

      const grouped = filteredResult.reduce((acc: GroupedPosts, post) => {
        let board = "";
        let main = "";
        let sub = "";

        if (post.grandGrandParentName) {
            board = getBoardLabel(post.grandGrandParentName);
            main = getBoardLabel(post.grandParentName) || "미분류";
            sub = post.parentName || "일반";
        } else {
            board = getBoardLabel(post.grandParentName) || "기타 게시판";
            main = post.parentName || "일반";
            sub = ""; // 소분류 없음 (UI에서 숨김 처리)
        }

        if (!acc[board]) acc[board] = {};
        if (!acc[board][main]) acc[board][main] = {};
        if (!acc[board][main][sub]) acc[board][main][sub] = [];

        acc[board][main][sub].push(post);
        return acc;
      }, {});

      setGroupedData(grouped);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handlePostClick = async (
    e: React.MouseEvent, 
    post: SearchedPost,
  ) => {
    e.preventDefault();
    
    const postData = await getPostData(post.id);
    setCurrentPost(postData);

    // 검색 결과의 부모 계층을 board / 메인 / 서브로 환산한다. (B안: 이름 + id 동반)
    // - 3단계(board > main > sub): grandGrandParent 존재 → board=grandGrandParent, main=grandParent, sub=parent
    // - 2단계(board > main):       grandGrandParent 없음  → board=grandParent,      main=parent,      sub 없음
    const has3Levels = !!post.grandGrandParentName;
    const boardName = (has3Levels ? post.grandGrandParentName : post.grandParentName) || "";
    const mainName = (has3Levels ? post.grandParentName : post.parentName) || "";
    const mainId = has3Levels ? post.grandParentId : post.parentId;
    const subName = has3Levels ? (post.parentName || "") : "";

    const params = new URLSearchParams();
    params.set('title', post?.title || '');

    if (boardName === PORTAL_BOARD_NAME) {
      // 질환 카테고리(/portal) 트리로 표시: 질환 카테고리 > 메인 > 서브 > post
      params.set('from', 'portal');
      if (mainId != null) params.set('category', String(mainId));
      if (mainName) params.set('categoryName', mainName);
      if (subName) params.set('subCategoryName', subName);
    } else {
      // 그 외 board → dedicated 페이지 트리로 표시 (board 파라미터가 있으면 breadcrumb 가 /dedicated 로 인식)
      // board 파라미터는 DedicatedBoard 에서 이름으로 매칭하므로 표시라벨이 아닌 원본 board.name 을 넘긴다.
      params.set('board', boardName);
      if (mainId != null) params.set('category', String(mainId));
      if (mainName) params.set('categoryName', mainName);
      if (subName) params.set('subCategoryName', subName);
    }

    // [롤백] 기존 검색 경로 표기로 되돌리려면 위 블록을 지우고 아래 주석을 해제
    // const params = new URLSearchParams();
    // params.set('from', 'search');
    // params.set('title', keyword);
    // params.set('postTitle', post?.title || '');

    navigate(`/post/${post.id}?${params.toString()}`);
  };

  const hasResults = Object.keys(groupedData).length > 0;

  if (loading) return <Center><Spin size="large" tip="Searching..." /></Center>;

  return (
    <StSearchContainer>
      <SearchHeader>
        <span className="keyword">'{searchedKeyword || keyword}'</span> 검색 결과
      </SearchHeader>

      {/* 오타 교정이 일어난 경우, 원래 검색어로 돌아갈 길을 함께 제공한다 */}
      {originalKeyword && (
        <CorrectionNotice>
          <div>
            다음에 대한 검색 결과 표시 중 <span className="corrected">{searchedKeyword}</span>
          </div>
          <button
            type="button"
            onClick={() =>
              navigate({
                pathname: "/search",
                // autoCorrect=false 가 없으면 서버가 다시 교정해서 같은 화면이 나온다
                search: `?${createSearchParams({ title: originalKeyword, autoCorrect: "false" })}`,
              })
            }
          >
            처음에 검색한 결과 <span className="origin">{originalKeyword}</span>
          </button>
        </CorrectionNotice>
      )}

      {hasResults ? (
        <ResultArea>
          {/* Board Loop */}
          {Object.entries(groupedData).map(([boardName, mainGroups]) => (
            <BoardSection key={boardName}>
              <BoardTitle>{boardName}</BoardTitle>
              
              <BoardContent>
                {/* Main Category Loop */}
                {Object.entries(mainGroups).map(([mainName, subGroups]) => (
                  <MainCategorySection key={mainName}>
                    <MainTitle>
                      <span className="text">{mainName}</span>
                    </MainTitle>
                    
                    <SubCategoryContainer>
                      {/* Sub Category Loop */}
                      {Object.entries(subGroups).map(([subName, posts]) => (
                        <SubSection key={subName || "no-sub"}>
                          {/* subName이 빈 문자열("")이면 타이틀 숨김 (consultation 게시판 등) */}
                          {subName && <SubTitle>{subName}</SubTitle>}
                          
                          <PostList>
                            {/* Post Loop */}
                            {posts?.map((post) => {
                              return (
                                <PostItem
                                  key={post?.id}
                                  onClick={(e) => handlePostClick(e, post)}
                                >
                                  <PostTitle $isIdMatch={post.isIdMatch}>
                                    {post.isIdMatch && <IdMatchBadge>ID 검색</IdMatchBadge>}
                                    {post.isSecret && (
                                      <LockOutlined style={{ marginRight: '6px', color: '#bfbfbf' }} />
                                    )}
                                    {highlight(post?.title ?? "", searchedKeyword || keyword)}
                                  </PostTitle>

                                  {/* 제목에 검색어가 없는 결과가 왜 나왔는지 보여준다 */}
                                  {post.snippet && (
                                    <PostSnippet>
                                      {highlight(post.snippet, searchedKeyword || keyword)}
                                    </PostSnippet>
                                  )}
                                </PostItem>
                              );
                            })}
                          </PostList>
                        </SubSection>
                      ))}
                    </SubCategoryContainer>
                  </MainCategorySection>
                ))}
              </BoardContent>
            </BoardSection>
          ))}
        </ResultArea>
      ) : (
        <Empty 
          image={Empty.PRESENTED_IMAGE_SIMPLE} 
          description="검색 결과가 없습니다." 
          style={{ marginTop: "100px" }}
        />
      )}
    </StSearchContainer>
  );
}

const StSearchContainer = styled.div`
  width: 95%;
  max-width: 1200px;
  margin: 3rem auto;
  min-height: 60vh;

  mark {
    background-color: #fff1b8;
    color: inherit;
    padding: 0 1px;
    border-radius: 2px;
  }
`;

const Center = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 50vh;
  width: 100%;
`;

const SearchHeader = styled.h2`
  font-size: 24px;
  margin-bottom: 2rem;
  color: #333;
  border-bottom: 1px solid #eee;
  padding-bottom: 1rem;

  .keyword {
    color: #0050b3;
    font-weight: 700;
  }
`;

// 오타 교정 안내. 사용자가 원래 검색어로 돌아갈 수 있어야 하므로 버튼을 함께 둔다
const CorrectionNotice = styled.div`
  margin: -1rem 0 2rem;
  padding: 10px 14px;
  background-color: #fffbe6;
  border: 1px solid #ffe58f;
  border-radius: 4px;
  font-size: 14px;
  color: #614700;

  .corrected {
    font-weight: 700;
    color: #0050b3;
  }

  button {
    margin-top: 4px;
    padding: 0;
    border: none;
    background: none;
    color: #614700;
    font-size: 13px;
    cursor: pointer;

    .origin {
      color: #0050b3;
      text-decoration: underline;
    }

    &:hover .origin {
      color: #003a8c;
    }
  }
`;

const ResultArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4rem;
`;

const BoardSection = styled.div`
  width: 100%;
`;

const BoardTitle = styled.div`
  font-size: 20px;
  font-weight: 800;
  color: #fff;
  background-color: #595959;
  padding: 8px 16px;
  border-radius: 4px;
  display: inline-block;
  margin-bottom: 1.5rem;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
`;

const BoardContent = styled.div`
  padding-left: 10px;
  display: flex;
  flex-direction: column;
  gap: 2.5rem;
`;

const MainCategorySection = styled.div`
  width: 100%;
`;

const MainTitle = styled.div`
  padding: 10px 10px;
  border-bottom: 2px solid #0050b3;
  margin-bottom: 1.2rem;
  background-color: rgba(0, 80, 179, 0.03);
  
  .text {
    font-size: 18px;
    font-weight: 700;
    color: #0050b3;
  }
`;

const SubCategoryContainer = styled.div`
  padding-left: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const SubSection = styled.div`
`;

const SubTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
  display: flex;
  align-items: center;

  &::before {
    content: "";
    display: inline-block;
    width: 3px;
    height: 15px;
    background-color: #0050b3;
    margin-right: 12px;
  }
`;

const PostList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-left: 15px;
  border-left: 1px solid #f0f0f0;
  margin-left: 6px;
`;

const PostTitle = styled.div<{ $isIdMatch?: boolean }>`
  color: #1890ff;
  font-size: 14.5px;
  width: fit-content;
  line-height: 1.6;
  transition: color 0.2s;
  ${({ $isIdMatch }) => $isIdMatch && `font-weight: 700;`}

  &::before {
    content: "•";
    margin-right: 10px;
    color: #bfbfbf;
  }
`;

const PostItem = styled.div`
  cursor: pointer;

  &:hover ${PostTitle} {
    text-decoration: underline;
    color: #0050b3;
  }
`;

// 본문 발췌. 제목 불릿 너비만큼 들여쓰고, 길면 두 줄에서 자른다
const PostSnippet = styled.div`
  margin: 2px 0 0 22px;
  font-size: 13px;
  line-height: 1.5;
  color: #8c8c8c;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

// post id 로 검색된 결과임을 나타내는 배지
const IdMatchBadge = styled.span`
  display: inline-block;
  margin-right: 8px;
  padding: 1px 7px;
  border-radius: 10px;
  background-color: #0050b3;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  line-height: 1.6;
  vertical-align: middle;
`;

export default SearchPage;